/**
 * App root - sets up routing and context providers for CiteOps.ai Dashboard
 *
 * Routes:
 * - /login            -> LoginPage (public)
 * - /auth/callback    -> AuthCallbackPage (public)
 * - /                 -> redirect to /dashboard
 * - Protected routes inside DashboardLayout:
 *   - /dashboard      -> DashboardPage
 *   - /scans          -> ScansPage
 *   - /scans/:id      -> ScanDetailPage
 *   - /competitors    -> CompetitorsPage (Pro+ gated with TierGate)
 *   - /monitoring     -> MonitoringPage (Pro+ gated with TierGate)
 *   - /team           -> TeamPage (Team gated)
 *   - /api-keys       -> ApiKeysPage (Team gated)
 *   - /settings       -> SettingsPage
 * - *                 -> NotFoundPage
 */

import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { gtagPageview } from './lib/gtag'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { Spinner } from './components/ui/Spinner'

// Route-level code splitting — each page is a separate chunk loaded on demand
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })))
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })))
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ScansPage = lazy(() => import('./pages/ScansPage').then(m => ({ default: m.ScansPage })))
const ScanDetailPage = lazy(() => import('./pages/ScanDetailPage').then(m => ({ default: m.ScanDetailPage })))
const CompetitorsPage = lazy(() => import('./pages/CompetitorsPage').then(m => ({ default: m.CompetitorsPage })))
const MonitoringPage = lazy(() => import('./pages/MonitoringPage').then(m => ({ default: m.MonitoringPage })))
const TeamPage = lazy(() => import('./pages/TeamPage').then(m => ({ default: m.TeamPage })))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

/** Full-screen loading fallback shown while a route chunk is being fetched. */
function PageLoader() {
  return (
    <div className="page-loader">
      <Spinner size="lg" />
    </div>
  )
}

/** Fires a GA4 page_view on every route change. */
function PageviewTracker() {
  const { pathname } = useLocation()
  useEffect(() => {
    gtagPageview(pathname)
  }, [pathname])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <PageviewTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Root redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Protected routes (require authentication) */}
              <Route element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/scans" element={<ScansPage />} />
                  <Route path="/scans/:id" element={<ScanDetailPage />} />
                  <Route path="/competitors" element={<CompetitorsPage />} />
                  <Route path="/monitoring" element={<MonitoringPage />} />
                  <Route path="/team" element={<TeamPage />} />
                  <Route path="/api-keys" element={<ApiKeysPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
